/*
 * © 2025 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
import { ProcessUpdatePresentation } from "@/presentation/processUpdates";
import {
  MachineType,
  OffsetAdjustmentAppliedAndCalculation,
  ProcessUpdatesTableType,
  ProcessAction,
} from "@centralwebteam/narwhal";
import { orderBy, uniqBy } from "lodash/fp";
import { ProcessUpdatePerf } from "@/store/state";

const tempProcessUpdate = (name = "") => {
  return {
    name: name,
    targetMachine: {
      name: name,
      type: undefined,
    },
    offsets: {
      exceeded: 0,
      applied: 0,
    },
    runMode: undefined,
    characteristics: {
      fails: 0,
      passes: 0,
    },
    jobName: "",
    measurementVerdict: "",
    sourceMachine: {
      name: "",
      type: undefined,
    },
    time: undefined,
    controlledOffset: "",
    displayName: "",
    totalUpdates: 0,
    lastOffsetAdjustment: undefined,
    calculatedCount: 0,
  };
};

export function getTableKey(
  item: ProcessUpdatePresentation | ProcessUpdatePerf,
  tableType: ProcessUpdatesTableType
) {
  switch (tableType) {
    case "Characteristic":
      return `${item.targetMachine?.name},${item.jobName},${item.sourceMachine!.name}`;
    case "Job":
      return `${item.targetMachine?.name},${item.jobName}`;
    case "Machine":
      return `${item.targetMachine?.name}`;
  }
}

export function getDetailTableKey(
  item: ProcessUpdatePresentation | ProcessUpdatePerf,
  tableType: ProcessUpdatesTableType
) {
  switch (tableType) {
    case "Characteristic":
      return `${item.targetMachine?.name},${item.jobName},${item.sourceMachine!.name},${item.controlledOffset}`;
    case "Job":
      return `${item.targetMachine?.name},${item.jobName}`;
    case "Machine":
      return `${item.targetMachine?.name},${item.controlledOffset}`;
  }
}

function getGroupingKey(
  update: ProcessUpdatePresentation,
  tableType: ProcessUpdatesTableType
): (string | undefined)[] {
  switch (tableType) {
    case "Characteristic":
      return [
        update.targetMachine?.name,
        update.jobName,
        update.sourceMachine?.name,
      ];
    case "Job":
      return [update.targetMachine?.name, update.jobName];
    case "Machine":
      return [update.targetMachine?.name];
  }
}

function getUniqueIdentifier(
  tableType: ProcessUpdatesTableType
): (pu: ProcessUpdatePresentation) => any[] {
  switch (tableType) {
    case "Characteristic":
      return (pu) => [
        pu.jobName,
        pu.measuredFeature,
        pu.measuredCharacteristic,
        pu.time,
      ];
    case "Job":
      return (pu) => [pu.jobName, pu.time];
    case "Machine":
      return (pu) => [pu.time];
  }
}

export async function compileTableUpdates(
  processUpdates: ProcessUpdatePresentation[],
  tableType: ProcessUpdatesTableType
) {
  const filteredUpdates = processUpdates.filter(
    (processUpdate) => processUpdate.grouping === tableType
  );

  if (filteredUpdates.length === 0) {
    return {
      processUpdatesPerf: {} as MappyAbsolute<ProcessUpdatePerf>,
      processUpdateDetails: {} as MappyAbsolute<ProcessUpdatePresentation[]>,
      processUpdateDetailsMore: {} as Dictionary<
        Dictionary<ProcessUpdatePresentation[]>
      >,
    };
  }

  const orderedToolOffsetAppliedEvents = orderBy(
    "time",
    "desc",
    filteredUpdates
  );

  const uniqueToolOffsetAppliedEvents = uniqBy(getUniqueIdentifier(tableType))(
    orderedToolOffsetAppliedEvents
  );

  const groupedToolOffsetAppliedEventsByKey = new Map<
    string,
    ProcessUpdatePresentation[]
  >();
  let processedUpdates = 0;
  for (const event of uniqueToolOffsetAppliedEvents) {
    const key = JSON.stringify(getGroupingKey(event, tableType));
    if (!groupedToolOffsetAppliedEventsByKey.has(key)) {
      groupedToolOffsetAppliedEventsByKey.set(key, []);
    }
    groupedToolOffsetAppliedEventsByKey.get(key)!.push(event);
  }

  const tempProcessUpdates: Map<string, ProcessUpdatePerf> = new Map();
  const tempProcessUpdatesDetails: Map<string, ProcessUpdatePresentation[]> =
    new Map();
  const processUpdateDetailsMore: Dictionary<
    Dictionary<ProcessUpdatePresentation[]>
  > = {};

  for (const [_, groupedToolOffsets] of groupedToolOffsetAppliedEventsByKey) {
    const uniqueGroupedToolOffsets: ProcessUpdatePresentation[] = [];
    const seen = new Set<string>();

    for (const item of groupedToolOffsets) {
      let hash: string;
      switch (tableType) {
        case "Characteristic":
          hash = `${item.targetMachine?.id}_${item.sourceMachine?.id}_${item.controlledOffset}_${item.jobName}`;
          break;
        case "Job":
          hash = `${item.targetMachine?.id}_${item.jobName}_${item.controlledOffset}_${item.correlationId}`;
          break;
        case "Machine":
          hash = `${item.targetMachine?.id}_${item.controlledOffset}_${item.correlationId}`;
          break;
      }

      if (!seen.has(hash)) {
        seen.add(hash);
        uniqueGroupedToolOffsets.push(item);
      }
    }

    for (let i = uniqueGroupedToolOffsets.length - 1; i >= 0; i--) {
      const event = uniqueGroupedToolOffsets[i];
      const tableKey = getTableKey(event, tableType);

      if (!tempProcessUpdates.has(tableKey)) {
        tempProcessUpdates.set(tableKey, tempProcessUpdate(tableKey));
      }

      const processUpdatesPerf = tempProcessUpdates.get(tableKey)!;
      processUpdatesPerf.name = tableKey;
      processUpdatesPerf.targetMachine.name = event.targetMachine!.name;
      processUpdatesPerf.targetMachine.type =
        (event.targetMachine!.type as MachineType) ?? undefined;
      event.lastOffsetAdjustment === "Exceeded"
        ? processUpdatesPerf.offsets.exceeded++
        : processUpdatesPerf.offsets.applied++;
      processUpdatesPerf.runMode = event.runMode;
      event.measurementVerdict === "Pass"
        ? processUpdatesPerf.characteristics.passes++
        : processUpdatesPerf.characteristics.fails++;
      processUpdatesPerf.jobName = event.jobName || "";
      processUpdatesPerf.measurementVerdict = event.measurementVerdict;
      processUpdatesPerf.sourceMachine = event.sourceMachine!;
      processUpdatesPerf.time = event.time;
      processUpdatesPerf.controlledOffset = event.controlledOffset;
      processUpdatesPerf.displayName = event.displayName!;
      processUpdatesPerf.lastOffsetAdjustment = event.lastOffsetAdjustment;
      if (event.lastOffsetAdjustment === "Calculated") {
        processUpdatesPerf.calculatedCount++;
      }

      if (tableType !== "Characteristic") {
        processUpdatesPerf.totalUpdates = uniqueGroupedToolOffsets.length;
      }

      if (!tempProcessUpdatesDetails.has(tableKey)) {
        tempProcessUpdatesDetails.set(tableKey, uniqueGroupedToolOffsets);
      }

      if (tableType === "Characteristic") {
        const correlationMap = new Map<string, Set<string>>();

        for (const update of uniqueToolOffsetAppliedEvents) {
          const outerKey = getTableKey(update, tableType);
          const innerKey = getDetailTableKey(update, tableType);

          if (!processUpdateDetailsMore[outerKey]) {
            processUpdateDetailsMore[outerKey] = {};
          }

          const mapKey = `${outerKey}_${innerKey}`;
          if (!correlationMap.has(mapKey)) {
            correlationMap.set(mapKey, new Set());
            processUpdateDetailsMore[outerKey][innerKey] = [];
          }

          if (!correlationMap.get(mapKey)!.has(update.correlationId)) {
            correlationMap.get(mapKey)!.add(update.correlationId);
            processUpdateDetailsMore[outerKey][innerKey].push(update);
          }
        }
      }

      // By yielding at regular intervals, we give the UI thread a chance
      // to process other events. This stops the UI from freezing. Yielding
      // every 100 updates is a balance between performance and responsiveness.
      if (processedUpdates % 100 === 0) {
        await new Promise((resolve) => setTimeout(resolve, 0));
      }
      processedUpdates++;
    }
  }

  const processUpdatesPerf = Object.fromEntries(
    tempProcessUpdates
  ) as MappyAbsolute<ProcessUpdatePerf>;
  const processUpdateDetails = Object.fromEntries(
    tempProcessUpdatesDetails
  ) as MappyAbsolute<ProcessUpdatePresentation[]>;

  if (tableType === "Characteristic") {
    Object.values(processUpdatesPerf).forEach((item) => {
      const keyForDetailTable = getTableKey(item, tableType);
      const dataForCount = processUpdateDetailsMore[keyForDetailTable];

      if (dataForCount) {
        item.totalUpdates = Object.values(dataForCount).reduce(
          (total, updates) => total + updates.length,
          0
        );
      } else {
        item.totalUpdates = 0;
      }
    });
  }

  return {
    processUpdatesPerf,
    processUpdateDetails,
    processUpdateDetailsMore,
  };
}

export async function computeAllGroupedTableData(
  processActions: ProcessAction[]
) {
  const processUpdatesPresentations = processActions.map(
    (item) =>
      new ProcessUpdatePresentation(
        item as OffsetAdjustmentAppliedAndCalculation
      )
  );
  const groupedData = {
    Characteristic: [] as ProcessUpdatePresentation[],
    Job: [] as ProcessUpdatePresentation[],
    Machine: [] as ProcessUpdatePresentation[],
  };

  for (const item of processUpdatesPresentations) {
    if (
      item.grouping === "Characteristic" ||
      item.grouping === "Job" ||
      item.grouping === "Machine"
    ) {
      groupedData[item.grouping].push(item);
    }
  }

  const characteristicData = await compileTableUpdates(
    groupedData.Characteristic,
    "Characteristic"
  );
  const jobData = await compileTableUpdates(groupedData.Job, "Job");
  const machineData = await compileTableUpdates(groupedData.Machine, "Machine");

  return {
    characteristic: characteristicData,
    job: jobData,
    machine: machineData,
  };
}
